// Timezone utility functions
export const timezoneUtils = {
  // List of common US timezones
  commonTimezones: [
    { value: 'America/New_York', label: 'Eastern Time (ET)', offset: -5 },
    { value: 'America/Chicago', label: 'Central Time (CT)', offset: -6 },
    { value: 'America/Denver', label: 'Mountain Time (MT)', offset: -7 },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)', offset: -8 },
    { value: 'America/Phoenix', label: 'Arizona Time (AZ)', offset: -7 },
    { value: 'America/Anchorage', label: 'Alaska Time (AK)', offset: -9 },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HI)', offset: -10 },
    { value: 'America/Halifax', label: 'Atlantic Time (AT)', offset: -4 },
    { value: 'America/St_Johns', label: 'Newfoundland Time (NT)', offset: -3.5 },
  ],

  // List of all available timezones for comprehensive options
  allTimezones: [
    // US Timezones
    { value: 'America/New_York', label: 'Eastern Time (ET)', region: 'United States' },
    { value: 'America/Chicago', label: 'Central Time (CT)', region: 'United States' },
    { value: 'America/Denver', label: 'Mountain Time (MT)', region: 'United States' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)', region: 'United States' },
    { value: 'America/Phoenix', label: 'Arizona Time (AZ)', region: 'United States' },
    { value: 'America/Anchorage', label: 'Alaska Time (AK)', region: 'United States' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HI)', region: 'United States' },

    // Canadian Timezones
    { value: 'America/Halifax', label: 'Atlantic Time (AT)', region: 'Canada' },
    { value: 'America/St_Johns', label: 'Newfoundland Time (NT)', region: 'Canada' },
    { value: 'America/Toronto', label: 'Eastern Time (Canada)', region: 'Canada' },
    { value: 'America/Winnipeg', label: 'Central Time (Canada)', region: 'Canada' },
    { value: 'America/Edmonton', label: 'Mountain Time (Canada)', region: 'Canada' },
    { value: 'America/Vancouver', label: 'Pacific Time (Canada)', region: 'Canada' },

    // European Timezones
    { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)', region: 'Europe' },
    { value: 'Europe/Paris', label: 'Central European Time (CET)', region: 'Europe' },
    { value: 'Europe/Berlin', label: 'Central European Time (CET)', region: 'Europe' },
    { value: 'Europe/Moscow', label: 'Moscow Time (MSK)', region: 'Europe' },

    // Asian Timezones
    { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)', region: 'Asia' },
    { value: 'Asia/Shanghai', label: 'China Standard Time (CST)', region: 'Asia' },
    { value: 'Asia/Dubai', label: 'Gulf Standard Time (GST)', region: 'Asia' },
    { value: 'Asia/Singapore', label: 'Singapore Time (SGT)', region: 'Asia' },

    // Australian Timezones
    { value: 'Australia/Sydney', label: 'Australian Eastern Time (AET)', region: 'Australia' },
    { value: 'Australia/Perth', label: 'Australian Western Time (AWT)', region: 'Australia' },

    // Additional major timezones
    { value: 'UTC', label: 'Coordinated Universal Time (UTC)', region: 'Universal' },
    { value: 'America/Sao_Paulo', label: 'Brasilia Time (BRT)', region: 'South America' },
    { value: 'Asia/Kolkata', label: 'India Standard Time (IST)', region: 'Asia' },
  ],

  // Get the user's current timezone
  getUserTimezone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  },

  // Get timezone offset for a given timezone
  getTimezoneOffset(timezone) {
    try {
      const date = new Date();
      const utcDate = new Date(date.toLocaleString("en-US", { timeZone: "UTC" }));
      const tzDate = new Date(date.toLocaleString("en-US", { timeZone: timezone }));
      return (tzDate - utcDate) / (1000 * 60 * 60);
    } catch (error) {
      console.warn(`Could not get timezone offset for ${timezone}:`, error);
      return 0;
    }
  },

  // Format date and time for a specific timezone
  formatDateTime(dateString, timezone, options = {}) {
    try {
      const date = new Date(dateString);
      const defaultOptions = {
        timeZone: timezone,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        ...options
      };

      return date.toLocaleString('en-US', defaultOptions);
    } catch (error) {
      console.warn(`Could not format date for timezone ${timezone}:`, error);
      return new Date(dateString).toLocaleString('en-US', options);
    }
  },

  // Format time only for a specific timezone
  formatTime(dateString, timezone, options = {}) {
    try {
      const date = new Date(dateString);
      const defaultOptions = {
        timeZone: timezone,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        ...options
      };

      return date.toLocaleTimeString('en-US', defaultOptions);
    } catch (error) {
      console.warn(`Could not format time for timezone ${timezone}:`, error);
      return new Date(dateString).toLocaleTimeString('en-US', options);
    }
  },

  // Format date only for a specific timezone
  formatDate(dateString, timezone, options = {}) {
    try {
      const date = new Date(dateString);
      const defaultOptions = {
        timeZone: timezone,
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        ...options
      };

      return date.toLocaleDateString('en-US', defaultOptions);
    } catch (error) {
      console.warn(`Could not format date for timezone ${timezone}:`, error);
      return new Date(dateString).toLocaleDateString('en-US', options);
    }
  },

  // Format date header for a specific timezone (long format)
  formatDateHeader(dateString, timezone, options = {}) {
    try {
      const date = new Date(dateString);
      const defaultOptions = {
        timeZone: timezone,
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        ...options
      };

      return date.toLocaleDateString('en-US', defaultOptions);
    } catch (error) {
      console.warn(`Could not format date header for timezone ${timezone}:`, error);
      return new Date(dateString).toLocaleDateString('en-US', options);
    }
  },

  // Get timezone abbreviation (ET, PT, etc.)
  getTimezoneAbbreviation(timezone) {
    try {
      const date = new Date();
      const options = { timeZone: timezone, timeZoneName: 'short' };
      const formatter = new Intl.DateTimeFormat('en-US', options);
      const parts = formatter.formatToParts(date);
      const timeZoneName = parts.find(part => part.type === 'timeZoneName')?.value;
      return timeZoneName || timezone;
    } catch (error) {
      console.warn(`Could not get timezone abbreviation for ${timezone}:`, error);
      return timezone;
    }
  },

  // Validate if a timezone identifier is valid
  isValidTimezone(timezone) {
    try {
      Intl.DateTimeFormat(undefined, { timeZone: timezone });
      return true;
    } catch (error) {
      return false;
    }
  },

  // Find timezone by value in the common timezones list
  findCommonTimezone(value) {
    return this.commonTimezones.find(tz => tz.value === value);
  },

  // Find timezone by value in all timezones list
  findAllTimezone(value) {
    return this.allTimezones.find(tz => tz.value === value);
  }
};